<template>
  <div class="sim" :class="{ presenting }">
    <!-- ================= header ================= -->
    <header class="sim-hero">
      <div class="sim-hero-text">
        <div class="zh-eyebrow"><span class="zh-dot"></span> Sales simulator · administrators only</div>
        <h1 class="zh-title" v-if="!presenting">What this forest can earn with <span>Zerticarbon</span></h1>
        <h1 class="zh-title" v-else>{{ form.reference || 'Your forest' }} <span>with Zerticarbon</span></h1>
        <p class="zh-sub" v-if="!presenting">Find the forest, choose how we would manage it and show the owner, year by year, what the CO₂ is worth.</p>
        <p class="zh-sub" v-else>{{ locationLabel }}{{ inputs.areaHa ? ` · ${fmtHa(inputs.areaHa)} ha` : '' }}</p>
      </div>
      <div class="sim-actions" v-if="!presenting">
        <router-link to="/simulations" class="sa-btn ghost"><i class="fas fa-folder-open"></i><span>Saved simulations</span></router-link>
        <button type="button" class="sa-btn ghost" @click="newSimulation"><i class="fas fa-plus"></i><span>New</span></button>
        <button type="button" class="sa-btn ghost money-toggle" :class="{ off: !showMoney }" @click="toggleMoney" :title="showMoney ? 'Hide the earnings and show the CO₂' : 'Show the earnings'">
          <i class="fas" :class="showMoney ? 'fa-euro-sign' : 'fa-eye-slash'"></i><span>{{ showMoney ? 'Hide €' : 'Show €' }}</span>
        </button>
        <button type="button" class="sa-btn ghost" @click="present" :disabled="!result"><i class="fas fa-desktop"></i><span>Present</span></button>
        <button type="button" class="sa-btn primary" @click="save" :disabled="saving || !model">
          <i class="fas" :class="saving ? 'fa-circle-notch fa-spin' : 'fa-save'"></i><span>{{ simId ? (dirty ? 'Save changes' : 'Saved') : 'Save' }}</span>
        </button>
      </div>
      <div v-else class="sim-actions">
        <button type="button" class="sa-btn exit" @click="toggleMoney"><i class="fas" :class="showMoney ? 'fa-eye-slash' : 'fa-euro-sign'"></i><span>{{ showMoney ? 'Hide €' : 'Show €' }}</span></button>
        <button type="button" class="sa-btn exit" @click="exitPresent"><i class="fas fa-compress"></i><span>Exit presentation</span></button>
      </div>
    </header>

    <div v-if="loadError" class="sim-error"><i class="fas fa-exclamation-triangle"></i> {{ loadError }}</div>
    <div v-else-if="!model || !inputs" class="sim-loading"><i class="fas fa-seedling fa-spin"></i> Loading the simulator…</div>

    <div v-else class="sim-grid">
      <!-- ================= inputs ================= -->
      <section class="sim-inputs">
        <!-- the forest -->
        <div class="sc sc-map">
          <div class="sc-head">
            <span class="sc-step">1</span>
            <div><h2>The forest</h2><p>Search the address or click on the map. We look for the stands, the cadastral parcel or the forest there.</p></div>
          </div>
          <div class="map-wrap">
            <sim-map ref="map" :features="mapFeatures" :drawn="inputs.selection.drawn" :marker="inputs.location"
                     :attributions="attributions" @locate="locate" @toggle="toggleFeature" @drawn="onDrawn" @error="toastError"/>
          </div>

          <div class="detect" v-if="detection.sources.length || inputs.location">
            <div class="detect-place" v-if="inputs.location">
              <i class="fas fa-map-marker-alt"></i> {{ locationLabel }}
            </div>
            <div class="detect-sources">
              <span v-for="s in detection.sources" :key="s.id" class="ds" :class="s.state">
                <i class="fas" :class="s.state === 'loading' ? 'fa-circle-notch fa-spin' : s.state === 'ok' ? 'fa-check' : 'fa-exclamation'"></i>
                {{ s.label }}<template v-if="s.state === 'ok'"> · {{ s.count ? `${s.count} found` : 'nothing here' }}</template>
                <button v-if="s.state === 'error'" type="button" @click="retrySource(s)">retry</button>
              </span>
            </div>
            <div class="detect-list" v-if="detection.features.length">
              <button v-for="f in detectionList" :key="f.properties.id" type="button" class="df" :class="{ on: f.properties.selected }" @click="toggleFeature(f.properties.id)">
                <i class="fas" :class="f.properties.selected ? 'fa-check-square' : 'fa-square'"></i>
                <span class="df-main">
                  <b>{{ f.properties.label }}</b>
                  <small>{{ describe(f) }}</small>
                </span>
                <span class="df-ha">{{ fmtHa(featureHa(f)) }} ha</span>
              </button>
              <div class="detect-bulk" v-if="standCount > 1">
                <button type="button" @click="selectAllStands(true)">Select all stands</button>
                <button type="button" @click="selectAllStands(false)">Clear</button>
              </div>
            </div>
          </div>

          <div class="property-row" v-if="propertySummary">
            <i class="fas fa-vector-square"></i>
            <span>Property {{ propertySummary.ids.join(', ') }}: <b>{{ fmtHa(propertySummary.propertyHa) }} ha</b><template v-if="propertySummary.forestHa > 0"> · <b>{{ fmtHa(propertySummary.forestHa) }} ha</b> of forest in {{ propertySummary.stands }} Forest Centre {{ propertySummary.stands === 1 ? 'stand' : 'stands' }}</template><template v-else> · no Forest Centre stands in it: the whole property is counted</template></span>
          </div>
          <div class="area-row">
            <label class="fld">
              <span>Forest area</span>
              <div class="fld-input"><input type="number" min="0" step="0.1" :value="round(inputs.areaHa, 2)" @input="setArea($event.target.value)"><em>ha</em></div>
            </label>
            <div class="area-source">
              <span class="badge-src" :class="inputs.areaSource">{{ areaSourceLabel }}</span>
              <button v-if="inputs.areaSource === 'manual' && selectionHa > 0" type="button" class="link" @click="useSelectionArea">Use the map's {{ fmtHa(selectionHa) }} ha</button>
              <button v-if="inputs.selection.drawn" type="button" class="link danger" @click="clearDrawn">Remove drawn boundary</button>
            </div>
          </div>
        </div>

        <!-- trees -->
        <div class="sc">
          <div class="sc-head">
            <span class="sc-step">2</span>
            <div><h2>Trees</h2><p>Species, density and age. Filled in from the forest data when we find it; change anything.</p></div>
          </div>
          <div class="auto-banner" v-if="inputs.detected && (inputs.auto.species || inputs.auto.density || inputs.auto.age)">
            <i class="fas fa-magic"></i>
            <span>From {{ inputs.detected.label }}: {{ inputs.detected.text }}</span>
          </div>
          <div class="species">
            <div v-for="(s, i) in inputs.species" :key="i" class="sp-row">
              <span class="sp-dot" :style="{ background: speciesColor(s.treeTypeId, i) }"></span>
              <b-form-select class="sp-select" :value="s.treeTypeId" :options="treeTypeOptions" @change="setSpecies(i, $event)" size="sm"/>
              <div class="sp-share">
                <input type="range" min="1" max="100" :value="s.share" @input="setShare(i, $event.target.value)">
                <span>{{ Math.round(shareOf(i)) }}%</span>
              </div>
              <button type="button" class="icon-btn" title="Remove" @click="removeSpecies(i)" :disabled="inputs.species.length < 2"><i class="fas fa-times"></i></button>
              <div class="sp-hint" v-if="typeOf(s.treeTypeId) && typeOf(s.treeTypeId).platform">
                Platform forests of this type: {{ fmt(typeOf(s.treeTypeId).platform.treesPerHa) }} trees/ha · {{ fmt(typeOf(s.treeTypeId).platform.co2PerHa) }} t CO₂/ha ({{ fmt(typeOf(s.treeTypeId).platform.forests) }} forests)
              </div>
            </div>
            <button type="button" class="link" @click="addSpecies"><i class="fas fa-plus"></i> Add a species</button>
          </div>
          <div class="two">
            <label class="fld">
              <span>Density <small v-if="inputs.auto.density && densityHint">{{ densityHint }}</small></span>
              <div class="fld-input"><input type="number" min="50" max="5000" step="10" :value="inputs.density" @input="setNum('density', $event.target.value, 'density')"><em>trees/ha</em></div>
              <input type="range" min="100" max="2500" step="10" :value="inputs.density" @input="setNum('density', $event.target.value, 'density')">
            </label>
            <label class="fld">
              <span>Mean age today</span>
              <div class="fld-input"><input type="number" min="1" max="200" step="1" :value="inputs.age" @input="setNum('age', $event.target.value, 'age')"><em>years</em></div>
              <input type="range" min="1" max="150" step="1" :value="inputs.age" @input="setNum('age', $event.target.value, 'age')">
            </label>
          </div>
        </div>

        <!-- additionality -->
        <div class="sc">
          <div class="sc-head">
            <span class="sc-step">3</span>
            <div><h2>Management that adds CO₂</h2><p>Additional CO₂ is paid at its own, higher price. Switch on what Zerticarbon would do in this forest.</p></div>
          </div>
          <div v-for="p in practiceCards" :key="p.key" class="practice" :class="{ on: p.on }">
            <div class="pr-head" @click="togglePractice(p.key)">
              <span class="pr-icon" :class="p.key"><i :class="p.icon"></i></span>
              <div class="pr-text"><b>{{ p.title }}</b><small>{{ p.text }}</small></div>
              <span class="pr-gain" v-if="p.gain" :class="{ neg: p.gain.net < 0 }">
                <template v-if="showMoney">
                  {{ p.on ? '' : 'could add ' }}{{ p.gain.net >= 0 ? '+' : '' }}{{ eurShort(p.gain.net) }}
                  <small>{{ p.gain.tons >= 0 ? '+' : '' }}{{ fmt(p.gain.tons) }} t additional</small>
                </template>
                <template v-else>
                  {{ p.on ? '' : 'could add ' }}{{ p.gain.tons >= 0 ? '+' : '' }}{{ fmt(p.gain.tons) }} t
                  <small>additional CO₂</small>
                </template>
              </span>
              <span class="pr-gain pr-hint" v-else-if="p.hint">{{ p.hint }}</span>
              <span class="switch" :class="{ on: p.on }"><span></span></span>
            </div>
            <div class="pr-body" v-if="p.on">
              <template v-if="p.key === 'fertilizing'">
                <div class="three">
                  <label class="fld"><span>Fertilizer</span>
                    <b-form-select size="sm" v-model="inputs.practices.fertilizing.fertilizer" :options="model.fertilizers"/>
                  </label>
                  <label class="fld"><span>First application</span>
                    <div class="fld-input"><input type="number" min="1" :max="inputs.period" v-model.number="inputs.practices.fertilizing.start"><em>{{ startYear + (inputs.practices.fertilizing.start || 1) }}</em></div>
                  </label>
                  <label class="fld"><span>Repeat every</span>
                    <div class="fld-input"><input type="number" min="0" max="50" v-model.number="inputs.practices.fertilizing.repeat"><em>{{ inputs.practices.fertilizing.repeat ? 'years' : 'once' }}</em></div>
                  </label>
                </div>
                <p class="pr-note">Only with Zerticarbon. Growth boost per year after the application, from the platform's fertilizing factors of each species.</p>
              </template>
              <template v-else-if="p.key === 'thinning'">
                <div class="three">
                  <label class="fld"><span>Thin in</span>
                    <div class="fld-input"><input type="number" min="1" :max="inputs.period" v-model.number="inputs.practices.thinning.start"><em>{{ startYear + (inputs.practices.thinning.start || 1) }}</em></div>
                  </label>
                  <label class="fld"><span>Down to</span>
                    <div class="fld-input"><input type="number" min="100" max="3000" step="50" v-model.number="inputs.practices.thinning.targetDensity"><em>trees/ha</em></div>
                  </label>
                  <label class="fld"><span>Conventional cuts more</span>
                    <div class="fld-input"><input type="number" min="0" max="100" v-model.number="inputs.practices.thinning.boost"><em>% trees</em></div>
                  </label>
                </div>
                <p class="pr-note">Our small machines remove {{ thinRemovedPct }}% of the trees; a conventional harvester would remove {{ convRemovedPct }}%. The trees we keep go on capturing CO₂: that is additional.</p>
              </template>
              <template v-else-if="p.key === 'finalCut'">
                <div class="two">
                  <label class="fld"><span>Final cut planned in</span>
                    <div class="fld-input"><input type="number" min="1" max="100" :value="inputs.practices.finalCut.plannedIn" @input="setCut($event.target.value)"><em>{{ startYear + (inputs.practices.finalCut.plannedIn || 1) }}</em></div>
                  </label>
                  <div class="pr-note pr-note--box" v-if="matureStands.count">
                    <i class="fas fa-info-circle"></i> {{ matureStands.count }} {{ matureStands.count === 1 ? 'stand is' : 'stands are' }} ({{ fmtHa(matureStands.ha) }} ha) mature for regeneration according to the Finnish Forest Centre.
                  </div>
                </div>
                <p class="pr-note">Without Zerticarbon every tree is felled that year (then replanted: the young stand captures almost nothing for years). Kept standing, its carbon and its growth are additional.</p>
              </template>
            </div>
          </div>
          <button type="button" class="link small" @click="showAdvanced = !showAdvanced"><i class="fas" :class="showAdvanced ? 'fa-chevron-up' : 'fa-chevron-down'"></i> Emissions of the forestry work</button>
          <div class="two adv" v-if="showAdvanced">
            <label class="fld"><span>Each fertilizing</span>
              <div class="fld-input"><input type="number" min="0" step="0.01" v-model.number="inputs.emissions.fertilizing"><em>t CO₂/ha</em></div>
            </label>
            <label class="fld"><span>The thinning</span>
              <div class="fld-input"><input type="number" min="0" step="0.01" v-model.number="inputs.emissions.thinning"><em>t CO₂/ha</em></div>
            </label>
            <p class="pr-note">ISO 14064-2: emissions of the machinery subtract from the additional CO₂.</p>
          </div>
        </div>

        <!-- prices -->
        <div class="sc">
          <div class="sc-head">
            <span class="sc-step">4</span>
            <div><h2>Prices and terms</h2><p>Change them for this owner. The defaults come from the administrators' settings.</p></div>
          </div>
          <div class="two">
            <label class="fld price base">
              <span><i class="dot base"></i> Base CO₂ price <small class="est" title="Estimated price: change it for each owner">estimate</small></span>
              <div class="fld-input"><input type="number" min="0" step="0.5" v-model.number="inputs.prices.base"><em>€/t</em></div>
              <input type="range" min="0" max="200" step="0.5" v-model.number="inputs.prices.base">
            </label>
            <label class="fld price add">
              <span><i class="dot add"></i> Additional CO₂ price <small class="est" title="Estimated price: change it for each owner">estimate</small></span>
              <div class="fld-input"><input type="number" min="0" step="0.5" v-model.number="inputs.prices.additional"><em>€/t</em></div>
              <input type="range" min="0" max="300" step="0.5" v-model.number="inputs.prices.additional">
            </label>
          </div>
          <div class="four">
            <label class="fld"><span>Price growth</span>
              <div class="fld-input"><input type="number" min="-50" max="100" step="0.5" v-model.number="inputs.prices.growth"><em>%/yr</em></div>
            </label>
            <label class="fld"><span>Zerticarbon fee</span>
              <div class="fld-input"><input type="number" min="0" max="100" step="1" v-model.number="inputs.prices.fee"><em>%</em></div>
            </label>
            <label class="fld"><span>Project length</span>
              <div class="fld-input"><input type="number" min="1" max="100" step="1" :value="inputs.period" @input="setPeriod($event.target.value)"><em>years</em></div>
            </label>
            <label class="fld"><span>Starting</span>
              <div class="fld-input"><input type="number" min="2000" max="2100" step="1" v-model.number="inputs.startYear"><em></em></div>
            </label>
          </div>
          <div class="defaults-row">
            <span>Defaults: {{ eur(defaults.basePrice) }} base · {{ eur(defaults.additionalPrice) }} additional · {{ defaults.fee }}% fee · {{ defaults.period }} years<template v-if="model.defaults.updatedBy"> (set by {{ model.defaults.updatedBy }})</template></span>
            <button type="button" class="link" @click="resetPrices">Use defaults</button>
            <button type="button" class="link" @click="saveDefaults">Make these the defaults</button>
          </div>
        </div>

        <!-- client -->
        <div class="sc">
          <div class="sc-head">
            <span class="sc-step"><i class="fas fa-user"></i></span>
            <div><h2>Owner and follow-up</h2><p>Saved with the simulation, only administrators see it.</p></div>
          </div>
          <div class="two">
            <label class="fld wide"><span>Reference name *</span><div class="fld-input"><input ref="reference" type="text" maxlength="160" v-model="form.reference" placeholder="e.g. Mäkelä estate, Savonlinna"></div></label>
            <label class="fld"><span>Status</span>
              <b-form-select size="sm" v-model="form.status" :options="statusOptions"/>
            </label>
          </div>
          <div class="two">
            <label class="fld"><span>Owner / contact</span><div class="fld-input"><input type="text" maxlength="160" v-model="form.contactName"></div></label>
            <label class="fld"><span>Company</span><div class="fld-input"><input type="text" maxlength="160" v-model="form.company"></div></label>
          </div>
          <div class="three">
            <label class="fld"><span>Email</span><div class="fld-input"><input type="email" maxlength="160" v-model="form.email"></div></label>
            <label class="fld"><span>Phone</span><div class="fld-input"><input type="tel" maxlength="60" v-model="form.phone"></div></label>
            <label class="fld" for="sim-follow-up"><span>Next follow-up</span><date-field id="sim-follow-up" v-model="form.followUp" compact/></label>
          </div>
          <label class="fld"><span>Notes</span><textarea rows="3" maxlength="20000" v-model="form.notes" placeholder="What the owner cares about, next steps…"></textarea></label>
          <div class="save-row">
            <span class="muted" v-if="savedInfo">{{ savedInfo }}</span>
            <button type="button" class="sa-btn primary" @click="save" :disabled="saving">
              <i class="fas" :class="saving ? 'fa-circle-notch fa-spin' : 'fa-save'"></i><span>{{ simId ? 'Save changes' : 'Save simulation' }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- ================= results ================= -->
      <section class="sim-results" v-if="result">
        <div class="res-hero">
          <div class="rh-glow"></div>
          <div class="rh-label">{{ showMoney ? `In ${totals.period} years this forest could earn its owner` : `In ${totals.period} years this forest could capture and certify` }}</div>
          <div class="rh-main" v-if="!showMoney" key="co2">
            <div class="rh-money">
              <div class="rh-value"><animated-number :value="tonsShown" locale="en-GB" :duration="900"/><small class="rh-unit">t CO₂</small></div>
              <div class="rh-sub">
                <span><b>{{ fmt(totals.tonsPerYear) }} t</b> a year</span>
                <span><b>{{ fmtDec(totals.tonsPerHaYear) }} t</b> per hectare a year</span>
              </div>
            </div>
            <div class="rh-co2" v-if="totals.addTons > 0">
              <span class="rh-co2-icon"><i class="fas fa-plus"></i></span>
              <div>
                <div class="rh-co2-value"><animated-number :value="addShown" locale="en-GB" :duration="900"/><small>t</small></div>
                <div class="rh-co2-sub">additional CO₂, thanks to the management</div>
              </div>
            </div>
          </div>
          <div class="rh-main" v-else key="money">
            <div class="rh-money">
              <div class="rh-value"><animated-number :value="totals.clientNet" prefix="€" locale="en-GB" :duration="900"/></div>
              <div class="rh-sub">
                <span><b>{{ eur(totals.perYear) }}</b> a year</span>
                <span><b>{{ eur(totals.perHaYear) }}</b> per hectare a year</span>
                <span v-if="inputs.prices.fee"><b>{{ eur(totals.gross) }}</b> before the {{ inputs.prices.fee }}% fee</span>
              </div>
            </div>
            <div class="rh-co2">
              <span class="rh-co2-icon"><i class="fas fa-cloud-download-alt"></i></span>
              <div>
                <div class="rh-co2-value"><animated-number :value="tonsShown" locale="en-GB" :duration="900"/><small>t CO₂</small></div>
                <div class="rh-co2-sub">certified · <b>{{ fmt(totals.tonsPerYear) }} t</b> a year<template v-if="totals.addTons > 0"> · <b>{{ fmt(addShown) }} t</b> additional</template></div>
              </div>
            </div>
          </div>
          <div class="split" v-if="!showMoney && totals.tons > 0">
            <div class="split-bar">
              <span class="base" :style="{ width: baseTonsPct + '%' }"></span>
              <span class="add" :style="{ width: (100 - baseTonsPct) + '%' }"></span>
            </div>
            <div class="split-legend">
              <span><i class="dot base"></i> Base CO₂ <b>{{ fmt(baseShown) }} t</b></span>
              <span>+ <i class="dot add"></i> Additional CO₂ <b>{{ fmt(addShown) }} t</b></span>
              <span>= <b>{{ fmt(tonsShown) }} t</b> certified</span>
            </div>
          </div>
          <div class="split" v-if="showMoney && totals.gross > 0">
            <div class="split-bar">
              <span class="base" :style="{ width: basePct + '%' }"></span>
              <span class="add" :style="{ width: (100 - basePct) + '%' }"></span>
            </div>
            <div class="split-legend">
              <span><i class="dot base"></i> Base CO₂ <b>{{ fmt(baseShown) }} t</b> × {{ eur(inputs.prices.base) }}<sup class="est-mark" title="Estimated price">est.</sup> = <b>{{ eur(totals.baseEur * feeKeep) }}</b></span>
              <span><i class="dot add"></i> Additional CO₂ <b>{{ fmt(addShown) }} t</b> × {{ eur(inputs.prices.additional) }}<sup class="est-mark" title="Estimated price">est.</sup> = <b>{{ eur(totals.addEur * feeKeep) }}</b></span>
            </div>
          </div>
          <div class="rh-chips">
            <span class="chip"><i class="fas fa-tree"></i> <b>{{ fmt(totals.storedToday) }} t</b> CO₂ stored in the forest today</span>
            <span class="chip"><i class="fas fa-layer-group"></i> <b>{{ fmtDec(totals.tonsPerHaYear) }} t</b> CO₂ per hectare a year</span>
            <span class="chip"><i class="fas fa-seedling"></i> <b>{{ fmt(totals.trees) }}</b> trees</span>
          </div>
        </div>

        <div class="res-scene">
          <sim-forest-scene ref="scene" :species="sceneSpecies" :density="inputs.density" :age="inputs.age" :period="totals.period" :start-year="startYear"
                            :years="result.years" :events="sceneEvents" :show-money="showMoney"/>
        </div>

        <div class="res-card">
          <div class="rc-tabs">
            <button v-for="tab in chartTabs" :key="tab.key" type="button" :class="{ on: shownTab === tab.key }" @click="chartTab = tab.key"><i :class="tab.icon"></i> {{ tab.label }}</button>
          </div>
          <div class="rc-chart">
            <apexchart :key="`${shownTab}:${showMoney}:${themeTick}`" :type="chart.type" height="100%" :options="chart.options" :series="chart.series"/>
          </div>
        </div>

        <div class="res-vs">
          <div class="vs-head">
            <h3>Without or with Zerticarbon?</h3>
            <span class="vs-delta" v-if="totals.tons > 0">{{ showMoney ? `+${eur(totals.clientNet)} and ` : '+' }}{{ fmt(tonsShown) }} t of certified CO₂</span>
          </div>
          <div class="res-compare">
            <div class="cmp without">
              <div class="cmp-title"><i class="fas fa-times-circle"></i> Without Zerticarbon</div>
              <div class="cmp-sub">{{ conventionalText }}</div>
              <div class="cmp-value">{{ showMoney ? eur(0) : '0 t' }}<small v-if="!showMoney"> CO₂ certified</small></div>
              <div class="cmp-section">Certified and paid</div>
              <dl class="sum">
                <div><dt>Base CO₂</dt><dd>0 t</dd></div>
                <div><dt>+ Additional CO₂</dt><dd>0 t</dd></div>
                <div class="total"><dt>= CO₂ certified and paid</dt><dd>0 t</dd></div>
              </dl>
              <div class="cmp-section">In the forest</div>
              <dl>
                <div><dt>CO₂ its growth captures</dt><dd>{{ fmt(totals.convTons) }} t</dd></div>
                <div><dt>Trees felled</dt><dd>{{ felledText(totals.convFelledShare) }}</dd></div>
                <div><dt>CO₂ in the forest in {{ totals.endYear }}</dt><dd>{{ fmt(totals.convStockEnd) }} t</dd></div>
              </dl>
              <p>Nobody measures, certifies or sells its CO₂.</p>
            </div>
            <div class="cmp with">
              <div class="cmp-title"><i class="fas fa-check-circle"></i> With Zerticarbon</div>
              <div class="cmp-sub">{{ zerticarbonText }}</div>
              <div class="cmp-value">{{ showMoney ? eur(totals.clientNet) : `${fmt(tonsShown)} t` }}<small v-if="!showMoney"> CO₂ certified</small></div>
              <div class="cmp-section">Certified and paid</div>
              <dl class="sum">
                <div><dt>Base CO₂ <small>the forest's own growth</small></dt><dd>{{ fmt(baseShown) }} t</dd></div>
                <div><dt>+ Additional CO₂ <small>from the management</small></dt><dd>{{ fmt(addShown) }} t</dd></div>
                <div v-for="b in additionalBreakdown" :key="b.key" class="part"><dt>{{ b.label }}</dt><dd>{{ b.tons >= 0 ? '+' : '' }}{{ fmt(Math.round(b.tons)) }} t</dd></div>
                <div class="total"><dt>= CO₂ certified and paid</dt><dd>{{ fmt(tonsShown) }} t</dd></div>
              </dl>
              <div class="cmp-section">In the forest</div>
              <dl>
                <div><dt>CO₂ its growth captures</dt><dd>{{ fmt(totals.growthTons) }} t<em v-if="growthDelta !== null" :class="{ neg: growthDelta < 0 }">{{ growthDelta >= 0 ? '+' : '' }}{{ growthDelta }}%</em></dd></div>
                <div><dt>Trees felled</dt><dd>{{ felledText(totals.felledShare) }}</dd></div>
                <div><dt>CO₂ in the forest in {{ totals.endYear }}</dt><dd>{{ fmt(totals.storedEnd) }} t</dd></div>
              </dl>
              <p v-if="totals.tons > totals.growthTons + 0.5" class="why"><i class="fas fa-info-circle"></i> The certified CO₂ can be more than what the growth captures: it also counts the carbon that stays in the trees we do not fell.</p>
              <p>Drone LiDAR scan, digital twin and certified CO₂ (ZCO2 tokens).</p>
            </div>
          </div>
          <div class="vs-bars">
            <div class="vb-row">
              <span class="vb-name">Without</span>
              <div class="vb-track"><i class="nat" :style="{ width: barPct(totals.convTons) + '%' }"></i></div>
              <b>{{ fmt(totals.convTons) }} t</b>
            </div>
            <div class="vb-row">
              <span class="vb-name">Zerticarbon</span>
              <div class="vb-track"><i class="base" :style="{ width: barPct(totals.baseTons) + '%' }"></i><i class="add" :style="{ width: barPct(Math.max(0, totals.addTons)) + '%' }"></i></div>
              <b>{{ fmt(tonsShown) }} t</b>
            </div>
            <small>Tonnes of CO₂ in {{ totals.period }} years: captured but not certified without Zerticarbon; certified (base + additional) with Zerticarbon.</small>
          </div>
        </div>

        <div class="res-eq-wrap">
          <div class="eqw-head">
            <span>The {{ fmt(tonsShown) }} t of certified CO₂ are as much as</span>
            <button type="button" class="info-btn" :class="{ on: showSources }" @click="showSources = !showSources" :aria-expanded="showSources ? 'true' : 'false'">
              <i class="fas fa-info-circle"></i> Where these figures come from
            </button>
          </div>
          <div class="res-eq">
            <div v-for="e in equivalences" :key="e.key" class="eq" :title="e.source">
              <i :class="e.icon"></i><b>{{ fmt(e.value) }}</b><span>{{ e.label }}</span>
            </div>
          </div>
          <transition name="eqs">
            <div class="eq-sources" v-if="showSources">
              <div v-for="e in equivalences" :key="e.key" class="eqs-row">
                <i :class="e.icon"></i>
                <div>
                  <b>{{ e.label }}</b> · {{ e.source }}
                  <a :href="e.url" target="_blank" rel="noopener noreferrer">{{ e.by }} <i class="fas fa-external-link-alt"></i></a>
                </div>
              </div>
              <p>Approximate figures, only to give an idea of how much CO₂ this is.</p>
            </div>
          </transition>
        </div>

        <div class="res-warn" v-if="result.warnings.length">
          <div v-for="(w, i) in result.warnings" :key="i"><i class="fas fa-info-circle"></i> {{ w }}</div>
        </div>
        <p class="res-note">
          Estimate with the Zerticarbon growth model of each species (the same formulas as the platform's forests); the CO₂ prices are estimates too.
          Not a binding offer: the final figures depend on the LiDAR scan of the forest and its certification.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import area from '@turf/area';
import bbox from '@turf/bbox';
import intersect from '@turf/intersect';
import { mapGetters } from 'vuex';
import VueApexCharts from 'vue-apexcharts';
import Simulator from '../api/Simulator';
import SimMap from '../components/Simulator/SimMap.vue';
import SimForestScene from '../components/Simulator/SimForestScene.vue';
import AnimatedNumber from '../components/Stats/AnimatedNumber.vue';
import DateField from '../components/structure/DateField.vue';
import { simulate, withPractice, summaryOf, defaultCutIn, genusOf, typicalDensity, EQUIVALENCES } from '../components/Simulator/engine';
import { apexBase, palette, themeState, watchTheme } from '../components/Stats/theme';
import { STATUS_LABELS } from '../components/Simulator/statuses';
import adminOnlyPage from '../mixins/adminOnlyPage';

const SPECIES_COLORS = ['#4f8c4a', '#159895', '#e0a52e', '#7c66e8', '#3b8fd4', '#e06a44'];
// a cadastral property is the owner's real boundary: it wins over forest stands, which win over OpenStreetMap
const SOURCE_PRIORITY = { 'es-cadastre': 3, 'fi-cadastre': 3, 'fi-stands': 2, osm: 1 };
const SOURCE_NAMES = { 'fi-cadastre': 'the Finnish cadastre', 'fi-stands': 'Finnish Forest Centre data', 'es-cadastre': 'the Spanish Cadastre', osm: 'OpenStreetMap' };
// the window of stands the API answers around a point (same as new-server/src/utils/sales-simulator.js)
const STANDS_WINDOW = 0.0045;
// the largest area the API gives stands for at once (~6 km)
const STANDS_MAX = [0.12, 0.06];

const asFeature = (f) => ({ type: 'Feature', geometry: f.geometry, properties: {} });
const boxesOverlap = (a, b) => a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
/** Hectares of `a` that fall inside `b` (0 when they do not touch or the geometry is broken). */
function insideHa(a, b) {
  try {
    if (!boxesOverlap(bbox(asFeature(a)), bbox(asFeature(b)))) return 0;
    const inter = intersect(asFeature(a), asFeature(b));
    return inter ? area(inter) / 10000 : 0;
  } catch (e) {
    return 0;
  }
}

const emptyForm = () => ({ reference: '', status: 'draft', contactName: '', company: '', email: '', phone: '', followUp: '', notes: '' });

export default {
  name: 'Simulator',
  mixins: [adminOnlyPage],
  components: { SimMap, SimForestScene, AnimatedNumber, DateField, apexchart: VueApexCharts },
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (!vm.getWeb2ActiveAccount) await vm.$store.dispatch('user/initWeb2');
      if (!vm.getWeb2ActiveAccount || vm.getWeb2ActiveAccount.role.name !== 'Administrator') vm.$router.replace('/');
    });
  },
  beforeRouteLeave(to, from, next) { this.confirmLeave(next); },
  beforeRouteUpdate(to, from, next) { this.confirmLeave(next); },
  data() {
    return {
      model: null,
      loadError: null,
      simId: null,
      saving: false,
      savedJson: null,
      savedMeta: null,
      leaving: false,
      form: emptyForm(),
      inputs: null,
      detection: { token: 0, sources: [], features: [], standBoxes: [] },
      presenting: false,
      chartTab: 'income',
      showAdvanced: false,
      showSources: false,
      stopTheme: null,
    };
  },
  computed: {
    ...mapGetters('user', ['getWeb2ActiveAccount']),
    themeTick() { return themeState.tick; },
    defaults() { return (this.model && this.model.defaults.values) || {}; },
    startYear() { return Number(this.inputs.startYear) || new Date().getFullYear(); },
    result() { return this.model && this.inputs ? simulate(this.inputs, this.model) : null; },
    totals() { return this.result.totals; },
    feeKeep() { return 1 - (Number(this.inputs.prices.fee) || 0) / 100; },
    showMoney() { return !(this.inputs && this.inputs.display && this.inputs.display.showMoney === false); },
    /** Rounded the way they are shown, so that base + additional (and the practices) add up on screen. */
    baseShown() { return Math.round(this.totals.baseTons); },
    addShown() {
      const parts = this.additionalBreakdown;
      return parts.length ? parts.reduce((a, b) => a + Math.round(b.tons), 0) : Math.round(this.totals.addTons);
    },
    tonsShown() { return this.baseShown + this.addShown; },
    baseTonsPct() { return this.totals.tons > 0 ? Math.max(0, Math.min(100, (this.totals.baseTons / this.totals.tons) * 100)) : 100; },
    /** The additional CO2 practice by practice (adds up to the additional total). */
    additionalBreakdown() {
      const t = this.totals;
      const p = this.inputs.practices;
      const rows = [];
      if (p.fertilizing.enabled) rows.push({ key: 'f', label: 'Fertilizing: faster growth', tons: t.parts.fertilizing - t.parts.emFertilizing });
      if (t.thinAt) rows.push({ key: 't', label: 'Thinning: trees we do not fell', tons: t.parts.thinning - t.parts.emThinning });
      if (t.cutAt) rows.push({ key: 'c', label: 'No final cut: carbon kept in the forest', tons: t.parts.finalCut });
      return rows;
    },
    basePct() { return this.totals.gross > 0 ? Math.max(0, Math.min(100, (this.totals.baseEur / this.totals.gross) * 100)) : 100; },
    statusOptions() { return Object.entries(STATUS_LABELS).map(([value, text]) => ({ value, text })); },
    treeTypeOptions() {
      const groups = { Finland: [], 'Spain and Mediterranean': [], Generic: [] };
      for (const t of this.model.treeTypes) {
        const g = /FIN/.test(t.name) ? 'Finland' : /^(Pinus|Quercus|Acer)/.test(t.name) ? 'Spain and Mediterranean' : 'Generic';
        groups[g].push({ value: t.id, text: t.name });
      }
      return Object.entries(groups).filter(([, o]) => o.length).map(([label, options]) => ({ label, options }));
    },
    locationLabel() {
      const l = this.inputs && this.inputs.location;
      if (!l) return 'No place chosen yet';
      return l.label || `${l.lat.toFixed(5)}, ${l.lng.toFixed(5)}`;
    },
    mapFeatures() {
      return this.detection.features.map((f) => ({ ...f, properties: { ...f.properties, mapLabel: this.mapLabel(f) } }));
    },
    detectionList() {
      return [...this.detection.features].sort((a, b) => (b.properties.selected - a.properties.selected) || (SOURCE_PRIORITY[b.properties.source] - SOURCE_PRIORITY[a.properties.source]) || (b.properties.containsPoint - a.properties.containsPoint)).slice(0, 40);
    },
    standCount() { return this.detection.features.filter((f) => f.properties.source === 'fi-stands').length; },
    attributions() {
      return [...new Set(this.detection.sources.filter((s) => s.state === 'ok' && s.count && s.attribution).map((s) => s.attribution))];
    },
    selected() { return this.detection.features.filter((f) => f.properties.selected); },
    /** The Finnish cadastral properties chosen: their boundary is the forest's. */
    propertiesSelected() { return this.selected.filter((f) => f.properties.source === 'fi-cadastre' && f.geometry); },
    /**
     * The stands that describe the forest, each with the hectares it counts for: inside the chosen properties only
     * the part inside counts (a stand across the boundary is partly the neighbour's); otherwise the chosen stands.
     */
    standsInUse() {
      const props = this.propertiesSelected;
      if (!props.length) return this.selected.filter((f) => f.properties.source === 'fi-stands').map((f) => ({ f, ha: f.properties.areaHa || 0 }));
      const out = [];
      for (const st of this.detection.features) {
        if (st.properties.source !== 'fi-stands' || !st.geometry) continue;
        let ha = 0;
        for (const p of props) ha += insideHa(st, p);
        if (ha >= 0.01) out.push({ f: st, ha: st.properties.areaHa ? Math.min(ha, st.properties.areaHa) : ha });
      }
      return out;
    },
    propertySummary() {
      const props = this.propertiesSelected;
      if (!props.length) return null;
      const propertyHa = props.reduce((a, f) => a + (f.properties.areaHa || 0), 0);
      const forestHa = this.standsInUse.reduce((a, s) => a + s.ha, 0);
      return { ids: props.map((f) => f.properties.propertyId), propertyHa, forestHa, stands: this.standsInUse.length };
    },
    selectionHa() {
      if (this.inputs.selection.drawn) return area(this.inputs.selection.drawn) / 10000;
      // one source only: the forest inside the properties (or the whole properties when no stand data covers them)
      const ps = this.propertySummary;
      if (ps) return ps.forestHa > 0 ? ps.forestHa : ps.propertyHa;
      return this.selected.reduce((a, f) => a + this.featureHa(f), 0);
    },
    /** What is saved with the simulation: the chosen features, and the stands that give the forest its data. */
    selectionToSave() {
      const out = new Map();
      for (const f of this.selected) out.set(f.properties.id, f);
      for (const s of this.standsInUse) out.set(s.f.properties.id, s.f);
      return [...out.values()];
    },
    areaSourceLabel() {
      return { manual: 'Entered by hand', drawn: 'Drawn on the map', 'fi-cadastre': 'Forest inside the cadastral property', 'fi-stands': 'Finnish Forest Centre stands', 'es-cadastre': 'Cadastral parcel', osm: 'OpenStreetMap forest' }[this.inputs.areaSource] || 'Entered by hand';
    },
    densityHint() {
      const d = this.inputs.detected;
      if (d && d.density) return `from ${d.label}`;
      const withPlatform = this.inputs.species.some((s) => { const t = this.typeOf(s.treeTypeId); return t && t.platform; });
      return withPlatform ? 'median of the platform forests' : 'typical for the species, adjust it';
    },
    matureStands() {
      const list = this.standsInUse.filter((s) => s.f.properties.matureForRegeneration);
      return { count: list.length, ha: list.reduce((a, s) => a + s.ha, 0) };
    },
    thinRemovedPct() {
      const t = this.inputs.practices.thinning;
      return this.inputs.density > t.targetDensity ? Math.round((1 - t.targetDensity / this.inputs.density) * 100) : 0;
    },
    practiceCards() {
      const p = this.inputs.practices;
      const now = this.totals;
      // what the practice itself adds over conventional management (never negative); switched off, what it would add
      const contribution = (totals, key) => {
        const emitted = key === 'fertilizing' ? totals.parts.emFertilizing : key === 'thinning' ? totals.parts.emThinning : 0;
        return { net: totals.partsEur[key], tons: totals.parts[key] - emitted };
      };
      const gain = (key) => {
        if (p[key].enabled) return contribution(now, key);
        let alt = withPractice(this.inputs, key, true);
        if (key === 'thinning') alt = { ...alt, practices: { ...alt.practices, thinning: { ...alt.practices.thinning, targetDensity: this.thinTarget() } } };
        return contribution(simulate(alt, this.model).totals, key);
      };
      const hint = (key) => {
        if (key === 'finalCut' && p.finalCut.plannedIn > this.totals.period) return `The planned cut (${this.startYear + p.finalCut.plannedIn}) is after the project`;
        if (key === 'thinning' && this.inputs.density <= 150) return 'The forest is already open';
        return null;
      };
      return [
        { key: 'fertilizing', icon: 'fas fa-seedling', title: 'Fertilizing', text: 'Only with Zerticarbon: wood ash or mineral fertilizer, the trees grow faster for up to 25 years.' },
        { key: 'thinning', icon: 'fas fa-cut', title: 'Thinning', text: `Conventional harvesters cut ${Number(p.thinning.boost) || 0}% more trees than our small machines: the trees we keep go on capturing CO₂.` },
        { key: 'finalCut', icon: 'fas fa-shield-alt', title: 'Postponed final cut', text: 'Without Zerticarbon every tree is felled; with us the forest stays standing and keeps capturing.' },
      ].map((c) => {
        const g = gain(c.key);
        const h = hint(c.key);
        return { ...c, on: !!p[c.key].enabled, gain: h && Math.abs(g.net) < 0.5 ? null : g, hint: h };
      });
    },
    sceneSpecies() {
      return this.inputs.species.map((s) => ({ genus: genusOf((this.typeOf(s.treeTypeId) || {}).name), share: Number(s.share) || 0 }));
    },
    sceneEvents() {
      const p = this.inputs.practices;
      const ev = {};
      if (p.fertilizing.enabled) {
        const list = [];
        const start = Math.max(1, Number(p.fertilizing.start) || 1);
        const repeat = Math.max(0, Number(p.fertilizing.repeat) || 0);
        for (let t = start; t <= this.totals.period; t += repeat || this.totals.period + 1) list.push(t);
        ev.fertilizing = list;
        ev.fertilizer = `${p.fertilizing.fertilizer} fertilizing`;
      }
      if (this.totals.thinAt) ev.thinning = { at: this.totals.thinAt, pct: this.totals.thinPct, convPct: this.totals.convThinPct, target: p.thinning.targetDensity };
      if (this.totals.cutAt) ev.finalCut = this.totals.cutAt;
      return ev;
    },
    activePractices() {
      return this.practiceCards.filter((p) => p.on).map((p) => p.title);
    },
    convRemovedPct() { return Math.round((this.totals.convThinPct || 0) * 100); },
    conventionalText() {
      const t = this.totals;
      const parts = [];
      if (t.thinAt) parts.push(`heavy harvesters cut ${this.convRemovedPct}% of the trees`);
      if (this.inputs.practices.fertilizing.enabled) parts.push('no fertilizer');
      if (t.cutAt) parts.push(`every tree felled in ${this.startYear + t.cutAt}`);
      return parts.length ? `Conventional management: ${parts.join(', ')}` : 'Conventional management, without measuring or certifying anything';
    },
    zerticarbonText() {
      const t = this.totals;
      const parts = ['drone LiDAR scan'];
      if (this.inputs.practices.fertilizing.enabled) parts.push(`${String(this.inputs.practices.fertilizing.fertilizer || '').toLowerCase()} fertilizing`);
      if (t.thinAt) parts.push(`small machines cut ${this.thinRemovedPct}%`);
      if (t.cutAt) parts.push('no final cut');
      return `${parts.join(', ').replace(/^./, (c) => c.toUpperCase())}`;
    },
    /** How much more (or less) CO2 the Zerticarbon forest captures than the conventionally managed one, in %. */
    growthDelta() {
      const n = this.totals.convTons;
      if (!(n > 0)) return null;
      const d = Math.round(((this.totals.growthTons - n) / n) * 100);
      return d === 0 ? null : d;
    },
    barMax() { return Math.max(1, this.totals.convTons, this.totals.baseTons + Math.max(0, this.totals.addTons)); },
    equivalences() {
      return EQUIVALENCES.map((e) => ({ ...e, value: this.totals.tons / e.per }));
    },
    /** Without the earnings there is no income chart: the CO2 one instead. */
    shownTab() { return !this.showMoney && this.chartTab === 'income' ? 'co2' : this.chartTab; },
    chartTabs() {
      if (!this.showMoney) {
        return [
          { key: 'co2', icon: 'fas fa-cloud-download-alt', label: 'CO₂ captured' },
          { key: 'yearly', icon: 'fas fa-chart-bar', label: 'Every year' },
          { key: 'carbon', icon: 'fas fa-tree', label: 'Forest carbon' },
        ];
      }
      return [
        { key: 'income', icon: 'fas fa-chart-area', label: 'Income' },
        { key: 'co2', icon: 'fas fa-cloud-download-alt', label: 'CO₂ captured' },
        { key: 'yearly', icon: 'fas fa-chart-bar', label: 'Every year' },
        { key: 'carbon', icon: 'fas fa-tree', label: 'Forest carbon' },
      ];
    },
    chart() {
      void themeState.tick;
      const c = palette();
      const years = this.result.years;
      const keep = this.feeKeep;
      const x = years.map((y) => String(y.year));
      const money = (v) => this.eurShort(v);
      const base = { chart: { stacked: true, animations: { enabled: true, speed: 500 } }, xaxis: { categories: x, tickAmount: Math.min(10, x.length) }, legend: { position: 'top', horizontalAlign: 'left' } };
      if (this.shownTab === 'yearly' && !this.showMoney) {
        return {
          type: 'bar',
          series: [
            { name: 'Base CO₂', data: years.map((y) => Math.round(y.baseTons)) },
            { name: 'Additional CO₂', data: years.map((y) => Math.round(y.addTons)) },
          ],
          options: apexBase({ ...base, colors: [c.green, c.teal], plotOptions: { bar: { columnWidth: '70%', borderRadius: 3 } }, dataLabels: { enabled: false }, yaxis: { labels: { formatter: (v) => `${this.fmt(v)} t` } }, tooltip: { y: { formatter: (v) => `${this.fmt(v)} t CO₂` } } }),
        };
      }
      if (this.shownTab === 'yearly') {
        return {
          type: 'bar',
          series: [
            { name: 'Base CO₂', data: years.map((y) => Math.round(y.baseEur * keep)) },
            { name: 'Additional CO₂', data: years.map((y) => Math.round(y.addEur * keep)) },
          ],
          options: apexBase({ ...base, colors: [c.green, c.teal], plotOptions: { bar: { columnWidth: '70%', borderRadius: 3 } }, dataLabels: { enabled: false }, yaxis: { labels: { formatter: money } }, tooltip: { y: { formatter: (v) => this.eur(v) } } }),
        };
      }
      const tonsAxis = { yaxis: { labels: { formatter: (v) => `${this.fmt(v)} t` } }, tooltip: { shared: true, y: { formatter: (v) => `${this.fmt(v)} t CO₂` } } };
      if (this.shownTab === 'co2') {
        return {
          type: 'area',
          series: [
            { name: 'Certified with Zerticarbon', data: years.map((y) => Math.round(y.cumBaseTons + y.cumAddTons)) },
            { name: 'of which additional', data: years.map((y) => Math.round(y.cumAddTons)) },
            { name: 'Without Zerticarbon (not certified)', data: years.map((y) => Math.round(y.cumConvTons)) },
          ],
          options: apexBase({ ...base, chart: { stacked: false }, colors: [c.green, c.teal, c.muted], stroke: { width: [3, 2, 2], curve: 'smooth', dashArray: [0, 0, 6] }, fill: { type: 'gradient', gradient: { opacityFrom: 0.42, opacityTo: 0.04 } }, dataLabels: { enabled: false }, ...tonsAxis }),
        };
      }
      if (this.shownTab === 'carbon') {
        const series = [
          { name: 'With Zerticarbon', data: years.map((y) => Math.round(y.projectStock)) },
          { name: 'Without Zerticarbon', data: years.map((y) => Math.round(y.convStock)) },
        ];
        return {
          type: 'area',
          series,
          options: apexBase({ ...base, chart: { stacked: false }, colors: [c.green, c.coral], stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 6] }, fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.04 } }, dataLabels: { enabled: false }, ...tonsAxis }),
        };
      }
      let b = 0;
      let a = 0;
      return {
        type: 'area',
        series: [
          { name: 'Base CO₂', data: years.map((y) => { b += y.baseEur * keep; return Math.round(b); }) },
          { name: 'Additional CO₂', data: years.map((y) => { a += y.addEur * keep; return Math.round(a); }) },
        ],
        options: apexBase({ ...base, colors: [c.green, c.teal], stroke: { width: 2, curve: 'smooth' }, fill: { type: 'gradient', gradient: { opacityFrom: 0.55, opacityTo: 0.15 } }, dataLabels: { enabled: false }, yaxis: { labels: { formatter: money } }, tooltip: { shared: true, y: { formatter: (v) => this.eur(v) } } }),
      };
    },
    payload() {
      return {
        reference: this.form.reference.trim(),
        status: this.form.status,
        contactName: this.form.contactName,
        company: this.form.company,
        email: this.form.email.trim(),
        phone: this.form.phone,
        followUp: this.form.followUp || null,
        notes: this.form.notes,
        locationLabel: this.inputs.location ? this.locationLabel : null,
        country: this.inputs.location ? this.inputs.location.country : null,
        lat: this.inputs.location ? this.inputs.location.lat : null,
        lng: this.inputs.location ? this.inputs.location.lng : null,
        areaHa: Number(this.inputs.areaHa) || 0,
        inputs: { ...this.inputs, selection: { drawn: this.inputs.selection.drawn, features: this.selectionToSave } },
      };
    },
    dirty() { return !!this.inputs && JSON.stringify(this.payload) !== this.savedJson; },
    savedInfo() {
      if (!this.savedMeta) return '';
      const when = new Date(this.savedMeta.updatedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
      return `${this.dirty ? 'Unsaved changes · last saved' : 'Saved'} ${when} by ${this.savedMeta.updatedBy || 'an administrator'}`;
    },
  },
  watch: {
    '$route.fullPath'() {
      // saving a new simulation only puts its id in the address: nothing to reload
      if (this.inputs && String(this.$route.params.id || '') === String(this.simId || '') && !this.$route.query.from) return;
      this.init();
    },
    presenting(on) { document.body.classList.toggle('sim-presenting', on); },
  },
  async created() {
    this.stopTheme = watchTheme();
    window.addEventListener('beforeunload', this.onUnload);
    document.addEventListener('fullscreenchange', this.onFullscreen);
    await this.init();
  },
  beforeDestroy() {
    if (this.stopTheme) this.stopTheme();
    window.removeEventListener('beforeunload', this.onUnload);
    document.removeEventListener('fullscreenchange', this.onFullscreen);
    document.body.classList.remove('sim-presenting');
  },
  methods: {
    // ---------- formatting
    fmt(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Number(v) || 0); },
    fmtHa(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: v < 10 ? 2 : 1 }).format(Number(v) || 0); },
    eur(v) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: Math.abs(v) < 100 && v % 1 ? 2 : 0 }).format(Number(v) || 0); },
    eurShort(v) {
      const n = Number(v) || 0;
      const abs = Math.abs(n);
      if (abs >= 1e6) return `€${(n / 1e6).toFixed(abs >= 1e7 ? 1 : 2)}M`;
      if (abs >= 1e4) return `€${Math.round(n / 1e3)}k`;
      return this.eur(n);
    },
    fmtDec(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: v < 10 ? 1 : 0 }).format(Number(v) || 0); },
    felledText(share) {
      if (!(share > 0)) return 'none';
      if (share >= 1) return `all ${this.fmt(this.totals.trees)}`;
      return `${Math.round(share * 100)}% · ${this.fmt(this.totals.trees * share)}`;
    },
    barPct(v) { return Math.max(0, Math.min(100, ((Number(v) || 0) / this.barMax) * 100)); },
    round(v, d) { return Math.round((Number(v) || 0) * 10 ** d) / 10 ** d; },
    toastError(message) { this.$toasted.show(message, { type: 'error', duration: 5000, theme: 'bubble', position: 'top-center' }); },
    toastOk(message) { this.$toasted.show(message, { type: 'success', duration: 3500, theme: 'bubble', position: 'top-center' }); },

    // ---------- loading
    async init() {
      this.loadError = null;
      try {
        if (!this.model) this.model = (await Simulator.getModel()).data;
      } catch (e) {
        this.loadError = e.response && e.response.status === 403 ? 'Only administrators can use the simulator.' : 'The simulator could not load the growth model. Try again in a moment.';
        return;
      }
      this.detection = { token: this.detection.token + 1, sources: [], features: [], standBoxes: [] };
      const id = this.$route.params.id;
      const from = this.$route.query.from;
      if (id || from) {
        try {
          const sim = (await Simulator.get(id || from)).data;
          this.load(sim, !id);
        } catch (e) {
          this.toastError('That simulation does not exist any more.');
          this.leaving = true;
          this.$router.replace('/simulator');
          this.leaving = false;
        }
        return;
      }
      this.simId = null;
      this.savedMeta = null;
      this.form = emptyForm();
      this.inputs = this.freshInputs();
      this.savedJson = JSON.stringify(this.payload);
    },
    freshInputs() {
      const d = this.defaults;
      const common = this.model.treeTypes.filter((t) => t.platform).sort((a, b) => b.platform.forests - a.platform.forests)[0] || this.model.treeTypes[0];
      const inputs = {
        version: 1,
        location: null,
        areaHa: 10,
        areaSource: 'manual',
        species: common ? [{ treeTypeId: common.id, share: 100 }] : [],
        density: (common && common.platform && common.platform.treesPerHa) || 900,
        age: 30,
        startYear: new Date().getFullYear(),
        period: d.period || 60,
        auto: { species: true, density: true, age: true, cut: true },
        detected: null,
        practices: {
          fertilizing: { enabled: false, fertilizer: this.model.fertilizers.includes('Wood ash') ? 'Wood ash' : this.model.fertilizers[0] || null, start: 1, repeat: 0 },
          thinning: { enabled: false, start: 1, targetDensity: 1000, boost: 20 },
          finalCut: { enabled: false, plannedIn: 30 },
        },
        emissions: { fertilizing: 0, thinning: 0 },
        prices: { base: d.basePrice, additional: d.additionalPrice, growth: d.priceGrowth, fee: d.fee },
        selection: { drawn: null, features: [] },
        display: { showMoney: true },
      };
      inputs.practices.finalCut.plannedIn = defaultCutIn(inputs, this.model);
      return inputs;
    },
    /** Opens a saved simulation (or a copy of it as a new one). */
    load(sim, asCopy) {
      const base = this.freshInputs();
      const saved = sim.inputs || {};
      const inputs = {
        ...base,
        ...saved,
        auto: { ...base.auto, ...(saved.auto || {}) },
        practices: {
          fertilizing: { ...base.practices.fertilizing, ...((saved.practices || {}).fertilizing || {}) },
          thinning: { ...base.practices.thinning, ...((saved.practices || {}).thinning || {}) },
          finalCut: { ...base.practices.finalCut, ...((saved.practices || {}).finalCut || {}) },
        },
        emissions: { ...base.emissions, ...(saved.emissions || {}) },
        display: { ...base.display, ...(saved.display || {}) },
        prices: { ...base.prices, ...(saved.prices || {}) },
        selection: { drawn: (saved.selection && saved.selection.drawn) || null, features: [] },
      };
      this.inputs = inputs;
      this.detection = {
        token: this.detection.token + 1,
        sources: [],
        features: ((saved.selection && saved.selection.features) || []).map((f) => ({ ...f, properties: { ...f.properties, selected: f.properties.selected !== false, manual: true } })),
        standBoxes: [],
      };
      this.form = {
        ...emptyForm(),
        reference: asCopy ? `${sim.reference} (copy)` : sim.reference,
        status: asCopy ? 'draft' : sim.status,
        contactName: sim.contactName || '', company: sim.company || '', email: sim.email || '', phone: sim.phone || '',
        followUp: sim.followUp || '', notes: sim.notes || '',
      };
      this.simId = asCopy ? null : sim.id;
      this.savedMeta = asCopy ? null : { updatedAt: sim.updatedAt, updatedBy: sim.updatedBy };
      this.savedJson = asCopy ? null : JSON.stringify(this.payload);
      this.$nextTick(() => {
        const map = this.$refs.map;
        if (!map) return;
        const geoms = [...this.detection.features.map((f) => f.geometry), inputs.selection.drawn].filter(Boolean);
        if (geoms.length) setTimeout(() => map.fitTo(geoms), 300);
        else if (inputs.location) map.flyTo(inputs.location.lat, inputs.location.lng, 14);
      });
    },

    // ---------- the place and its forest
    async locate({ lat, lng, label, country }) {
      this.inputs.location = { lat, lng, label: label || null, country: country || (this.inputs.location && this.inputs.location.country) || null };
      if (!label) this.reverseGeocode(lat, lng);
      await this.runDetection(lat, lng);
    },
    async reverseGeocode(lat, lng) {
      try {
        const { MAPBOX_TOKEN } = await import('../utils/mapbox');
        const json = await (await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1&language=en&types=place,locality,district,region`)).json();
        const f = json.features && json.features[0];
        if (f && this.inputs.location && this.inputs.location.lat === lat) {
          const country = ((f.context || []).find((c) => String(c.id).startsWith('country')) || {}).short_code || (f.properties && f.properties.short_code);
          this.inputs.location = { ...this.inputs.location, label: `${f.place_name} (${lat.toFixed(4)}, ${lng.toFixed(4)})`, country: country ? String(country).slice(0, 2).toUpperCase() : this.inputs.location.country };
        }
      } catch (e) { /* the coordinates are label enough */ }
    },
    async runDetection(lat, lng) {
      const token = this.detection.token + 1;
      // features the user picked by hand stay; the rest is replaced by what is found at the new point
      const kept = this.detection.features.filter((f) => f.properties.manual && f.properties.selected);
      this.detection = { token, sources: [], features: kept, standBoxes: [] };
      let list;
      try {
        list = (await Simulator.detectSources(lat, lng)).data.sources;
      } catch (e) {
        this.toastError('Could not look for the forest here. Draw the boundary or type the area.');
        return;
      }
      if (token !== this.detection.token) return;
      this.detection.sources = list.map((s) => ({ ...s, state: 'loading', count: 0, lat, lng }));
      list.forEach((s) => this.fetchSource(token, s.id, lat, lng));
    },
    async fetchSource(token, id, lat, lng) {
      let res;
      try {
        res = (await Simulator.detect(lat, lng, id)).data;
      } catch (e) {
        res = { ok: false };
      }
      if (token !== this.detection.token) return;
      const src = this.detection.sources.find((s) => s.id === id);
      if (!src) return;
      if (!res.ok) { src.state = 'error'; return; }
      src.state = 'ok';
      src.count = res.features.length;
      src.attribution = res.attribution;
      const known = new Set(this.detection.features.map((f) => f.properties.id));
      const incoming = res.features.filter((f) => !known.has(f.properties.id)).map((f) => ({ ...f, properties: { ...f.properties, selected: false, auto: false } }));
      this.detection.features = [...this.detection.features, ...incoming];
      if (id === 'fi-stands') {
        const dLng = STANDS_WINDOW / Math.cos((lat * Math.PI) / 180);
        this.detection.standBoxes.push([lng - dLng, lat - STANDS_WINDOW, lng + dLng, lat + STANDS_WINDOW]);
      }
      this.autoSelect();
      const geoms = this.selected.map((f) => f.geometry).filter(Boolean);
      if (geoms.length && this.$refs.map) this.$refs.map.fitTo(geoms);
      this.coverProperties();
    },
    /** A property larger than the window of stands around the point: ask the Forest Centre for the rest of it. */
    async coverProperties() {
      const props = this.propertiesSelected;
      const src = this.detection.sources.find((s) => s.id === 'fi-stands');
      if (!props.length || !src || src.state !== 'ok') return;
      const bb = bbox({ type: 'FeatureCollection', features: props.map(asFeature) });
      if (this.detection.standBoxes.some((b) => b[0] <= bb[0] && b[1] <= bb[1] && b[2] >= bb[2] && b[3] >= bb[3])) return;
      // never more than the API takes at once: centred on the property
      const cx = (bb[0] + bb[2]) / 2;
      const cy = (bb[1] + bb[3]) / 2;
      const w = Math.min(bb[2] - bb[0], STANDS_MAX[0] - 0.001) / 2;
      const h = Math.min(bb[3] - bb[1], STANDS_MAX[1] - 0.001) / 2;
      const box = [cx - w, cy - h, cx + w, cy + h];
      const token = this.detection.token;
      this.detection.standBoxes.push(box); // asked once, even if it fails
      let res;
      try {
        res = (await Simulator.detectArea(src.lat, src.lng, 'fi-stands', box)).data;
      } catch (e) {
        return;
      }
      if (token !== this.detection.token || !res.ok) return;
      const known = new Set(this.detection.features.map((f) => f.properties.id));
      const incoming = res.features.filter((f) => !known.has(f.properties.id)).map((f) => ({ ...f, properties: { ...f.properties, selected: false, auto: false } }));
      if (!incoming.length) return;
      this.detection.features = [...this.detection.features, ...incoming];
      src.count += incoming.length;
      this.applySelection();
    },
    retrySource(s) { s.state = 'loading'; this.fetchSource(this.detection.token, s.id, s.lat, s.lng); },
    /** Selects what contains the point, from the most precise source that has it (unless the user chose by hand). */
    autoSelect() {
      if (this.detection.features.some((f) => f.properties.manual)) return;
      const containing = this.detection.features.filter((f) => f.properties.containsPoint && f.geometry);
      const best = Math.max(0, ...containing.map((f) => SOURCE_PRIORITY[f.properties.source] || 0));
      for (const f of this.detection.features) {
        const want = f.properties.containsPoint && (SOURCE_PRIORITY[f.properties.source] || 0) === best && !(f.properties.tooLarge);
        f.properties.selected = want;
        f.properties.auto = want;
      }
      this.applySelection();
    },
    toggleFeature(id) {
      const f = this.detection.features.find((x) => x.properties.id === id);
      if (!f) return;
      if (!this.detection.features.some((x) => x.properties.manual)) this.detection.features.forEach((x) => { x.properties.manual = true; });
      const on = !f.properties.selected;
      // the area comes from one source only: a property and the stands inside it would count the same land twice
      if (on) this.detection.features.forEach((x) => { if (x.properties.source !== f.properties.source) x.properties.selected = false; });
      f.properties.selected = on;
      f.properties.manual = true;
      this.detection.features = [...this.detection.features];
      this.applySelection();
      this.coverProperties();
    },
    selectAllStands(on) {
      this.detection.features.forEach((f) => {
        f.properties.manual = true;
        if (f.properties.source === 'fi-stands') f.properties.selected = on;
        else if (on) f.properties.selected = false;
      });
      this.detection.features = [...this.detection.features];
      this.applySelection();
    },
    onDrawn(geometry) {
      this.inputs.selection.drawn = geometry;
      this.applySelection();
    },
    clearDrawn() {
      this.inputs.selection.drawn = null;
      this.applySelection();
    },
    featureHa(f) {
      const p = f.properties;
      return p.source === 'es-cadastre' && p.forestHa ? p.forestHa : (p.areaHa || 0);
    },
    /** Area, species, density and age from the selection (each one only while the user has not typed it). */
    applySelection() {
      const inputs = this.inputs;
      if (inputs.selection.drawn) {
        inputs.areaHa = this.round(area(inputs.selection.drawn) / 10000, 2);
        inputs.areaSource = 'drawn';
      } else if (this.propertiesSelected.length) {
        inputs.areaHa = this.round(this.selectionHa, 2);
        inputs.areaSource = this.propertySummary.forestHa > 0 ? 'fi-cadastre' : 'manual';
      } else if (this.selected.length) {
        inputs.areaHa = this.round(this.selectionHa, 2);
        const sources = new Set(this.selected.map((f) => f.properties.source));
        inputs.areaSource = sources.size === 1 ? [...sources][0] : 'manual';
      } else if (inputs.areaSource !== 'manual') {
        inputs.areaSource = 'manual';
      }

      // the forest's data: the stands inside the chosen properties (by the hectares inside), or the chosen features
      const inProperty = this.propertiesSelected.length > 0;
      const entries = inProperty
        ? this.standsInUse.filter((s) => s.f.properties.mix && s.f.properties.mix.length).map((s) => ({ f: s.f, w: s.ha }))
        : this.selected.filter((f) => f.properties.mix && f.properties.mix.length).map((f) => ({ f, w: this.featureHa(f) || 1 }));
      const withData = entries.map((e) => e.f);
      if (!withData.length) { inputs.detected = null; return; }
      const weightOf = new Map(entries.map((e) => [e.f, e.w || 0.0001]));
      const weight = (f) => weightOf.get(f) || 0.0001;
      const totalW = withData.reduce((a, f) => a + weight(f), 0);
      const mix = new Map();
      for (const f of withData) for (const m of f.properties.mix) if (m.treeTypeId) mix.set(m.treeTypeId, (mix.get(m.treeTypeId) || 0) + m.share * weight(f));
      const stems = withData.filter((f) => f.properties.stemCount > 0);
      const ages = withData.filter((f) => f.properties.meanAge > 0);
      const wAvg = (list, key) => list.reduce((a, f) => a + f.properties[key] * weight(f), 0) / list.reduce((a, f) => a + weight(f), 0);
      const src = withData[0].properties.source;
      const detected = {
        label: inProperty ? 'the Forest Centre stands inside the property' : (SOURCE_NAMES[src] || 'the map'),
        source: inProperty ? 'fi-cadastre' : src,
        count: withData.length,
        density: stems.length ? Math.round(wAvg(stems, 'stemCount')) : null,
        age: ages.length ? Math.round(wAvg(ages, 'meanAge')) : null,
      };
      const species = [...mix].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([treeTypeId, v]) => ({ treeTypeId, share: Math.max(1, Math.round((v / totalW) * 100)) }));
      if (inputs.auto.species && species.length) inputs.species = species;
      if (inputs.auto.density) inputs.density = detected.density || this.platformDensity(inputs.species) || inputs.density;
      if (inputs.auto.age && detected.age) inputs.age = detected.age;
      if (inputs.auto.cut) inputs.practices.finalCut.plannedIn = this.matureStands.count ? 1 : defaultCutIn(inputs, this.model);
      const names = species.slice(0, 3).map((s) => `${(this.typeOf(s.treeTypeId) || {}).name} ${s.share}%`).join(', ');
      detected.text = [
        inProperty ? `property ${this.propertySummary.ids.join(', ')}` : null,
        `${withData.length} ${src === 'fi-stands' ? (withData.length === 1 ? 'stand' : 'stands') : 'area'}`,
        names,
        detected.density ? `${this.fmt(detected.density)} trees/ha` : null,
        detected.age ? `${detected.age} years` : null,
      ].filter(Boolean).join(' · ');
      inputs.detected = detected;
    },
    describe(f) {
      const p = f.properties;
      if (p.source === 'fi-cadastre') {
        const ps = p.selected ? this.propertySummary : null;
        return [`Cadastre ${p.propertyId}`, `${p.parcels} ${p.parcels === 1 ? 'parcel' : 'parcels'}`, ps && ps.forestHa > 0 ? `${this.fmtHa(ps.forestHa)} ha of forest in ${ps.stands} stands` : null, p.containsPoint ? 'at the point' : 'neighbour'].filter(Boolean).join(' · ');
      }
      if (p.source === 'fi-stands') {
        return [p.mainSpecies, p.meanAge ? `${p.meanAge} y` : null, p.stemCount ? `${this.fmt(p.stemCount)} trees/ha` : null, p.development, p.fertility].filter(Boolean).join(' · ');
      }
      if (p.source === 'es-cadastre') {
        const uses = (p.landUse || []).slice(0, 3).map((u) => `${u.name.toLowerCase()} ${this.fmtHa(u.ha)} ha`).join(', ');
        return `Cadastre ${p.refcat}${uses ? ` · ${uses}` : ''}`;
      }
      return [p.leafType ? `${p.leafType} forest` : 'Forest in OpenStreetMap', p.containsPoint ? 'at the point' : 'nearby', p.tooLarge ? 'very large area: check it' : null].filter(Boolean).join(' · ');
    },
    mapLabel(f) {
      const p = f.properties;
      if (p.source === 'fi-stands') return `${p.label}${p.meanAge ? ` · ${p.meanAge} y` : ''}`;
      if (p.source === 'fi-cadastre') return p.propertyId;
      return '';
    },
    setArea(v) {
      this.inputs.areaHa = Math.max(0, Number(v) || 0);
      this.inputs.areaSource = 'manual';
    },
    useSelectionArea() { this.applySelection(); },

    // ---------- trees
    typeOf(id) { return this.model.treeTypes.find((t) => t.id === id); },
    speciesColor(id, i) { const t = this.typeOf(id); return (t && t.color) || SPECIES_COLORS[i % SPECIES_COLORS.length]; },
    shareOf(i) {
      const total = this.inputs.species.reduce((a, s) => a + (Number(s.share) || 0), 0) || 1;
      return ((Number(this.inputs.species[i].share) || 0) / total) * 100;
    },
    platformDensity(species) {
      let w = 0;
      let sum = 0;
      for (const s of species) {
        const t = this.typeOf(s.treeTypeId);
        const d = t && ((t.platform && t.platform.treesPerHa) || t.densityTree || typicalDensity(t.name));
        if (d) { sum += d * (Number(s.share) || 0); w += Number(s.share) || 0; }
      }
      return w ? Math.round(sum / w) : null;
    },
    speciesChanged() {
      this.inputs.auto.species = false;
      if (this.inputs.auto.density && !(this.inputs.detected && this.inputs.detected.density)) {
        this.inputs.density = this.platformDensity(this.inputs.species) || this.inputs.density;
      }
      if (this.inputs.auto.cut) this.inputs.practices.finalCut.plannedIn = defaultCutIn(this.inputs, this.model);
    },
    setSpecies(i, id) { this.inputs.species[i].treeTypeId = id; this.speciesChanged(); },
    setShare(i, v) { this.inputs.species[i].share = Number(v); this.inputs.auto.species = false; },
    addSpecies() {
      const used = new Set(this.inputs.species.map((s) => s.treeTypeId));
      const next = this.model.treeTypes.filter((t) => t.platform && !used.has(t.id)).sort((a, b) => b.platform.forests - a.platform.forests)[0] || this.model.treeTypes.find((t) => !used.has(t.id));
      if (!next) return;
      this.inputs.species.push({ treeTypeId: next.id, share: 25 });
      this.speciesChanged();
    },
    removeSpecies(i) { this.inputs.species.splice(i, 1); this.speciesChanged(); },
    setNum(key, v, autoKey) {
      this.inputs[key] = Math.max(0, Number(v) || 0);
      if (autoKey) this.inputs.auto[autoKey] = false;
      if (key === 'age' && this.inputs.auto.cut) this.inputs.practices.finalCut.plannedIn = defaultCutIn(this.inputs, this.model);
    },
    setPeriod(v) { this.inputs.period = Math.max(1, Math.min(100, Math.round(Number(v) || 1))); },
    setCut(v) { this.inputs.practices.finalCut.plannedIn = Math.max(1, Math.round(Number(v) || 1)); this.inputs.auto.cut = false; },
    toggleMoney() {
      this.inputs.display = { ...(this.inputs.display || {}), showMoney: !this.showMoney };
    },
    togglePractice(key) {
      const practice = this.inputs.practices[key];
      practice.enabled = !practice.enabled;
      if (key === 'thinning' && practice.enabled && practice.targetDensity >= this.inputs.density) practice.targetDensity = this.thinTarget();
    },
    /** Thinning target: the platform's 1,000 trees/ha, or 70% of a forest that already has fewer. */
    thinTarget() {
      const target = Number(this.inputs.practices.thinning.targetDensity) || 1000;
      if (target < this.inputs.density) return target;
      return Math.max(100, Math.round((this.inputs.density * 0.7) / 50) * 50);
    },

    // ---------- prices
    resetPrices() {
      const d = this.defaults;
      this.inputs.prices = { base: d.basePrice, additional: d.additionalPrice, growth: d.priceGrowth, fee: d.fee };
      this.inputs.period = d.period;
    },
    async saveDefaults() {
      const p = this.inputs.prices;
      const values = { basePrice: Number(p.base) || 0, additionalPrice: Number(p.additional) || 0, priceGrowth: Number(p.growth) || 0, fee: Number(p.fee) || 0, period: Number(this.inputs.period) || 30 };
      const answer = await Swal.fire({
        title: 'Make these the defaults?',
        html: `New simulations of every administrator will start with <b>${this.eur(values.basePrice)}</b> base, <b>${this.eur(values.additionalPrice)}</b> additional, ${values.priceGrowth}%/yr growth, ${values.fee}% fee and ${values.period} years.`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Save defaults',
      });
      if (!answer.isConfirmed) return;
      try {
        this.model.defaults = (await Simulator.saveDefaults(values)).data;
        this.toastOk('Default prices saved');
      } catch (e) {
        this.toastError((e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Could not save the defaults');
      }
    },

    // ---------- save
    async save() {
      if (!this.form.reference.trim()) {
        this.toastError('Give the simulation a reference name first');
        this.$nextTick(() => { const el = this.$refs.reference; if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); } });
        return;
      }
      this.saving = true;
      const body = { ...this.payload, summary: summaryOf(this.result, this.inputs) };
      try {
        const saved = (this.simId ? await Simulator.update(this.simId, body) : await Simulator.create(body)).data;
        this.savedJson = JSON.stringify(this.payload);
        this.savedMeta = { updatedAt: saved.updatedAt, updatedBy: saved.updatedBy };
        const isNew = !this.simId;
        this.simId = saved.id;
        this.toastOk(isNew ? 'Simulation saved' : 'Changes saved');
        if (isNew || this.$route.query.from) {
          this.leaving = true;
          await this.$router.replace(`/simulator/${saved.id}`).catch(() => {});
          this.leaving = false;
        }
      } catch (e) {
        this.toastError((e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Could not save the simulation');
      } finally {
        this.saving = false;
      }
    },
    newSimulation() {
      if (this.$route.path === '/simulator' && !this.$route.query.from) {
        if (this.dirty) {
          Swal.fire({ title: 'Start a new simulation?', text: 'The changes to this one are not saved.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Start new' })
            .then((a) => { if (a.isConfirmed) this.init(); });
        } else this.init();
        return;
      }
      this.$router.push('/simulator');
    },

    // ---------- presentation
    present() {
      this.presenting = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen().catch(() => {});
      setTimeout(() => this.$refs.scene && this.$refs.scene.play(), 700);
    },
    exitPresent() {
      this.presenting = false;
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    },
    onFullscreen() { if (!document.fullscreenElement && this.presenting) this.presenting = false; },
    async confirmLeave(next) {
      if (!this.dirty || this.leaving) return next();
      const answer = await Swal.fire({ title: 'Leave without saving?', text: 'The changes to this simulation are not saved.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Leave', cancelButtonText: 'Stay' });
      next(answer.isConfirmed);
    },
    onUnload(e) {
      if (this.dirty) { e.preventDefault(); e.returnValue = ''; }
    },
  },
};
</script>

<style lang="scss">
/* presentation: the page covers the app, the menus are hidden */
body.sim-presenting {
  overflow: hidden;

  .sidebar, .header-container, .app-footer { display: none !important; }
  .main-wrapper { margin-left: 0 !important; width: 100% !important; z-index: auto !important; }
}

/* dark theme tweaks (the theme attribute is on <html>, outside the scoped styles) */
[data-theme="dark"] .sim {
  .badge-src.fi-stands { color: #f4c25b; }
  .badge-src.es-cadastre { color: #8cc7f2; }
  .pr-note--box, .res-warn { color: #f4c25b; }
  .ds.ok { color: var(--accent-green-light); }
}
</style>

<style scoped lang="scss">
@import '../components/Stats/intro.scss';

.sim {
  padding: 4px 0 40px;
  --base-c: var(--accent-green);
  --add-c: var(--co2-accent, #159895);
}

.sim-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.sim-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.sa-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-main);
  font-weight: 700;
  font-size: 0.86rem;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover { transform: translateY(-1px); box-shadow: var(--shadow-md); text-decoration: none; color: var(--text-heading); }
  &:disabled { opacity: 0.55; transform: none; box-shadow: none; }
  &.primary { background: var(--accent-gradient); color: #fff; border-color: transparent; box-shadow: var(--shadow-glow); }
  &.exit { background: rgba(12, 24, 16, 0.7); color: #fff; border-color: transparent; }
}

.sim-loading, .sim-error {
  padding: 60px 20px;
  text-align: center;
  color: var(--text-muted);
  font-size: 1rem;
  i { color: var(--accent-green); margin-right: 6px; }
}
.sim-error i { color: #e06a44; }

.sim-grid {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 20px;
  align-items: start;
}

.sim-inputs { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.sim-results { display: flex; flex-direction: column; gap: 16px; min-width: 0; position: sticky; top: 84px; }

/* ---------- cards */
.sc {
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  padding: 18px;
  animation: sc-in 0.5s cubic-bezier(.2, .8, .2, 1) both;
}
// each card is its own layer (the entrance animation's transform): the one being edited goes on top, so an open
// calendar (Next follow-up) is not hidden under the next card
.sc:focus-within { position: relative; z-index: 5; }
.sc:nth-child(2) { animation-delay: 0.06s; }
.sc:nth-child(3) { animation-delay: 0.12s; }
.sc:nth-child(4) { animation-delay: 0.18s; }
@keyframes sc-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }

.sc-head {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;

  h2 { margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-heading); }
  p { margin: 2px 0 0; font-size: 0.8rem; color: var(--text-muted); }
}

.sc-step {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: #fff;
  background: var(--accent-gradient);
  box-shadow: var(--shadow-glow);
  font-size: 0.9rem;
}

.map-wrap { height: 380px; margin: 0 -4px; }

.detect { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.detect-place { font-size: 0.84rem; font-weight: 700; color: var(--text-heading); i { color: var(--accent-green); } }
.detect-sources { display: flex; flex-wrap: wrap; gap: 6px; }

.ds {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  background: var(--bg-primary);
  color: var(--text-muted);
  border: 1px solid var(--border-color);

  &.ok { color: var(--accent-green-dark); border-color: rgba(var(--accent-green-rgb), 0.35); }
  &.error { color: #c4532f; border-color: rgba(224, 106, 68, 0.4); }
  button { border: 0; background: transparent; color: inherit; text-decoration: underline; font-weight: 800; padding: 0; }
}

.detect-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 240px;
  overflow: auto;
  padding-right: 2px;
}

.df {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: transparent;
  text-align: left;
  color: var(--text-main);
  transition: background 0.15s, border-color 0.15s;

  > i { color: var(--text-muted); }
  &.on { background: rgba(var(--accent-green-rgb), 0.1); border-color: rgba(var(--accent-green-rgb), 0.45); > i { color: var(--accent-green); } }
  &:hover { border-color: rgba(var(--accent-green-rgb), 0.45); }
}
.df-main { flex: 1; min-width: 0; display: flex; flex-direction: column; b { font-size: 0.84rem; } small { color: var(--text-muted); font-size: 0.72rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } }
.df-ha { font-weight: 800; font-size: 0.82rem; color: var(--text-heading); white-space: nowrap; }
.detect-bulk { display: flex; gap: 12px; button { border: 0; background: none; color: var(--accent-green); font-size: 0.76rem; font-weight: 700; padding: 0; } }

.property-row { display: flex; gap: 8px; align-items: flex-start; margin-top: 10px; padding: 8px 12px; border-radius: 12px; font-size: 0.78rem; color: var(--text-main); background: rgba(95, 180, 240, 0.12); i { color: #3b8fd4; margin-top: 3px; } b { color: var(--text-heading); } }
.area-row { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; margin-top: 12px; }
.area-row .fld { width: 180px; }
.area-source { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding-bottom: 8px; }
.badge-src.fi-cadastre { background: rgba(95, 180, 240, 0.18); color: #2f6fa8; }
.badge-src {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  background: var(--bg-primary);
  color: var(--text-muted);
  &.drawn { background: rgba(255, 255, 255, 0.1); color: var(--text-heading); border: 1px dashed var(--text-muted); }
  &.fi-stands { background: rgba(244, 194, 91, 0.18); color: #a06d12; }
  &.es-cadastre { background: rgba(95, 180, 240, 0.18); color: #2f6fa8; }
  &.osm { background: rgba(var(--accent-green-rgb), 0.15); color: var(--accent-green-dark); }
}

/* ---------- fields */
.fld {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  min-width: 0;

  > span { font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; small { text-transform: none; letter-spacing: 0; font-weight: 600; color: var(--accent-green); margin-left: 4px; } }
  input[type=range] { accent-color: var(--accent-green); width: 100%; }
  textarea, .fld-input {
    border: 1px solid var(--border-color);
    border-radius: 12px;
    background: var(--bg-secondary);
    color: var(--text-main);
  }
  textarea { padding: 8px 12px; font-size: 0.88rem; resize: vertical; outline: none; &:focus { border-color: var(--accent-green); } }
  ::v-deep select { border-radius: 12px; height: 38px; background-color: var(--bg-secondary); color: var(--text-main); border-color: var(--border-color); }
}

.fld-input {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 10px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within { border-color: var(--accent-green); box-shadow: 0 0 0 3px rgba(var(--accent-green-rgb), 0.15); }
  input { flex: 1; min-width: 0; border: 0; outline: none; background: transparent; color: var(--text-heading); font-weight: 700; font-size: 0.92rem; }
  em { font-style: normal; font-size: 0.74rem; color: var(--text-muted); white-space: nowrap; margin-left: 6px; }
}

.price .fld-input input { font-size: 1.15rem; font-family: 'Sora', sans-serif; }
.price.base .fld-input { border-left: 4px solid var(--base-c); }
.price.add .fld-input { border-left: 4px solid var(--add-c); }

.two, .three, .four { display: grid; gap: 12px; margin-bottom: 12px; }
.two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.three { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.four { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.fld.wide { grid-column: span 1; }

.dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 4px; vertical-align: -1px; &.base { background: var(--base-c); } &.add { background: var(--add-c); } }

.link {
  border: 0;
  background: none;
  padding: 0;
  color: var(--accent-green);
  font-weight: 700;
  font-size: 0.8rem;
  &:hover { text-decoration: underline; }
  &.danger { color: #c4532f; }
  &.small { font-size: 0.76rem; margin-top: 4px; }
}

.auto-banner {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 9px 12px;
  margin-bottom: 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: var(--accent-green-dark);
  background: rgba(var(--accent-green-rgb), 0.1);
  i { margin-top: 3px; }
}

.species { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.sp-row {
  display: grid;
  grid-template-columns: 12px minmax(0, 1.3fr) minmax(0, 1fr) 30px;
  align-items: center;
  gap: 10px;
}
.sp-dot { width: 12px; height: 12px; border-radius: 50%; }
.sp-share { display: flex; align-items: center; gap: 8px; input { flex: 1; accent-color: var(--accent-green); } span { width: 38px; text-align: right; font-weight: 800; font-size: 0.84rem; color: var(--text-heading); } }
.sp-hint { grid-column: 2 / -1; margin-top: -6px; font-size: 0.72rem; color: var(--text-muted); }
.icon-btn { border: 0; width: 30px; height: 30px; border-radius: 9px; background: var(--bg-primary); color: var(--text-muted); &:hover:not(:disabled) { color: #c4532f; } &:disabled { opacity: 0.35; } }

/* ---------- practices */
.practice {
  border: 1px solid var(--border-color);
  border-radius: 16px;
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;

  &.on { border-color: rgba(21, 152, 149, 0.55); box-shadow: 0 10px 30px -18px rgba(21, 152, 149, 0.8); background: linear-gradient(180deg, rgba(21, 152, 149, 0.06), transparent 60%); }
}
.pr-head { display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer; user-select: none; }
.pr-icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  &.fertilizing { background: linear-gradient(135deg, #9ed77f, #3d7a3f); }
  &.thinning { background: linear-gradient(135deg, #f4c25b, #d08a1e); }
  &.finalCut { background: linear-gradient(135deg, #2fd0c4, #159895); }
}
.pr-text { flex: 1; min-width: 0; display: flex; flex-direction: column; b { color: var(--text-heading); font-size: 0.92rem; } small { color: var(--text-muted); font-size: 0.75rem; } }
.sp-select {
  height: 36px;
  border-radius: 12px;
  border-color: var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-heading);
  font-weight: 700;
}
.est { margin-left: 4px; font-size: 0.62rem; font-weight: 600; font-style: italic; text-transform: none; letter-spacing: 0; color: var(--text-muted); opacity: 0.85; cursor: help; }
.est-mark { margin-left: 2px; font-size: 0.6rem; font-style: italic; opacity: 0.7; cursor: help; }
.pr-gain.pr-hint { max-width: 160px; white-space: normal; font-size: 0.72rem; font-weight: 700; color: var(--text-muted); }
.pr-gain {
  text-align: right;
  font-weight: 800;
  font-size: 0.88rem;
  color: var(--add-c);
  white-space: nowrap;
  small { display: block; font-weight: 600; font-size: 0.7rem; color: var(--text-muted); }
  &.neg { color: #c4532f; }
}
.switch {
  flex: none;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: var(--border-color);
  position: relative;
  transition: background 0.25s;
  span { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25); transition: transform 0.25s cubic-bezier(.2, .8, .2, 1); }
  &.on { background: var(--add-c); span { transform: translateX(18px); } }
}
.pr-body { padding: 4px 14px 12px; animation: sc-in 0.35s ease both; }
.pr-note { margin: 0; font-size: 0.74rem; color: var(--text-muted); }
.pr-note--box { align-self: end; padding: 8px 10px; border-radius: 10px; background: rgba(244, 194, 91, 0.14); color: #8a5d10; }
.adv { margin-top: 10px; }

.defaults-row { display: flex; flex-wrap: wrap; gap: 6px 14px; align-items: center; font-size: 0.76rem; color: var(--text-muted); }
.save-row { display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
.muted { color: var(--text-muted); font-size: 0.76rem; }

/* ---------- results */
.res-hero {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 22px 24px;
  color: #fff;
  background: radial-gradient(circle at 85% -10%, rgba(47, 208, 196, 0.45), transparent 50%), linear-gradient(135deg, #1d4a2a 0%, #12301b 60%, #0c2214 100%);
  box-shadow: var(--shadow-lg);
}
.rh-glow { position: absolute; width: 320px; height: 320px; right: -120px; bottom: -160px; border-radius: 50%; background: radial-gradient(circle, rgba(158, 215, 127, 0.35), transparent 70%); animation: glow 6s ease-in-out infinite alternate; }
@keyframes glow { to { transform: translate(-40px, -30px) scale(1.15); } }
.rh-label { position: relative; font-size: 0.86rem; opacity: 0.85; }
.rh-main { position: relative; display: flex; align-items: flex-end; justify-content: space-between; gap: 14px 24px; flex-wrap: wrap; }
.rh-money { min-width: 0; }
.rh-co2 {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 18px;
  background: rgba(47, 208, 196, 0.14);
  border: 1px solid rgba(126, 224, 214, 0.35);
  animation: co2-in 0.6s cubic-bezier(.2, .8, .2, 1) 0.15s both;
}
@keyframes co2-in { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: none; } }
.rh-co2-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #0c2214;
  background: linear-gradient(135deg, #c9f7b3, #2fd0c4);
  box-shadow: 0 0 0 0 rgba(126, 224, 214, 0.5);
  animation: co2-ping 2.6s infinite;
}
@keyframes co2-ping { 0% { box-shadow: 0 0 0 0 rgba(126, 224, 214, 0.5); } 100% { box-shadow: 0 0 0 14px rgba(126, 224, 214, 0); } }
.rh-co2-value {
  font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  line-height: 1.05;
  color: #7ee0d6;
  font-variant-numeric: tabular-nums;
  small { font-size: 0.5em; margin-left: 6px; color: #c9f7b3; font-weight: 700; }
}
.rh-co2-sub { font-size: 0.76rem; opacity: 0.9; b { color: #c9f7b3; } }
.rh-value {
  position: relative;
  font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  line-height: 1.05;
  letter-spacing: -0.02em;
  background: linear-gradient(90deg, #ffffff, #c9f7b3, #7ee0d6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-variant-numeric: tabular-nums;
}
.rh-sub { position: relative; display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 4px; font-size: 0.86rem; opacity: 0.9; b { color: #c9f7b3; } }
.split { position: relative; margin-top: 16px; }
.split-bar { display: flex; height: 12px; border-radius: 999px; overflow: hidden; background: rgba(255, 255, 255, 0.12); span { height: 100%; transition: width 0.6s cubic-bezier(.2, .8, .2, 1); } .base { background: linear-gradient(90deg, #6bb15e, #9ed77f); } .add { background: linear-gradient(90deg, #2fd0c4, #7ee0d6); } }
.split-legend { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 14px; margin-top: 8px; font-size: 0.78rem; opacity: 0.95; .dot.base { background: #9ed77f; } .dot.add { background: #2fd0c4; } }
.rh-chips { position: relative; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.chip { padding: 6px 12px; border-radius: 999px; background: rgba(255, 255, 255, 0.1); font-size: 0.78rem; i { color: #9ed77f; margin-right: 4px; } }

.res-scene { border-radius: 20px; overflow: hidden; box-shadow: var(--shadow-md); height: 320px; }
.res-scene > * { height: 100%; }

.res-card { background: var(--card-bg); border: 1px solid var(--border-subtle); border-radius: 20px; box-shadow: var(--shadow-md); padding: 14px 16px 6px; }
.rc-tabs { display: flex; gap: 6px; flex-wrap: wrap; button { border: 0; padding: 6px 12px; border-radius: 10px; background: var(--bg-primary); color: var(--text-muted); font-weight: 700; font-size: 0.78rem; &.on { background: var(--accent-gradient); color: #fff; } } }
.rc-chart { height: 280px; margin-top: 6px; }

.res-vs { background: var(--card-bg); border: 1px solid var(--border-subtle); border-radius: 20px; box-shadow: var(--shadow-md); padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.vs-head { display: flex; align-items: baseline; justify-content: space-between; gap: 6px 12px; flex-wrap: wrap; h3 { margin: 0; font-size: 1rem; font-weight: 800; color: var(--text-heading); } }
.vs-delta { font-size: 0.8rem; font-weight: 800; color: var(--accent-green); }
.res-compare { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.cmp-sub { font-size: 0.74rem; color: var(--text-muted); margin-top: 1px; }
.cmp dl { margin: 8px 0 0; display: flex; flex-direction: column; gap: 4px; }
.cmp dl > div { display: flex; justify-content: space-between; gap: 10px; padding: 5px 0; border-top: 1px dashed var(--border-color); font-size: 0.78rem; }
.cmp dt { font-weight: 600; color: var(--text-muted); }
.cmp dd { margin: 0; font-weight: 800; color: var(--text-heading); white-space: nowrap; em { font-style: normal; margin-left: 6px; color: var(--accent-green); font-size: 0.72rem; &.neg { color: #c4532f; } } }
.cmp p { margin: 8px 0 0; font-size: 0.72rem; color: var(--text-muted); }
.without dd { color: var(--text-muted); }
.vs-bars { display: flex; flex-direction: column; gap: 8px; small { font-size: 0.7rem; color: var(--text-muted); } }
.vb-row { display: grid; grid-template-columns: 90px minmax(0, 1fr) 84px; align-items: center; gap: 10px; b { text-align: right; font-size: 0.82rem; color: var(--text-heading); font-variant-numeric: tabular-nums; } }
.vb-name { font-size: 0.76rem; font-weight: 700; color: var(--text-muted); }
.vb-track { display: flex; height: 14px; border-radius: 999px; overflow: hidden; background: var(--bg-primary); i { height: 100%; transition: width 0.7s cubic-bezier(.2, .8, .2, 1); } .nat { background: repeating-linear-gradient(135deg, #a7b5aa 0 6px, #c3cec6 6px 12px); } .base { background: linear-gradient(90deg, #6bb15e, #9ed77f); } .add { background: linear-gradient(90deg, #2fd0c4, #7ee0d6); } }
.cmp {
  border-radius: 18px;
  padding: 14px 16px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  ul { margin: 6px 0 0; padding-left: 18px; font-size: 0.78rem; color: var(--text-muted); }
  &.with { border-color: rgba(var(--accent-green-rgb), 0.5); background: linear-gradient(160deg, rgba(var(--accent-green-rgb), 0.12), var(--card-bg) 70%); }
}
.cmp-title { font-weight: 800; font-size: 0.84rem; color: var(--text-heading); .without & i { color: #e06a44; } .with & i { color: var(--accent-green); } }
.cmp-section { margin-top: 10px; font-size: 0.66rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); }
.cmp dl.sum { margin-top: 4px; }
.cmp dl.sum dt small { display: block; font-size: 0.66rem; font-weight: 500; }
.cmp dl.sum > div.part { border-top: 0; padding: 0 0 2px 14px; font-size: 0.72rem; dt { font-weight: 500; } dd { font-weight: 700; color: var(--add-c); } }
.cmp dl.sum > div.total { border-top: 2px solid var(--text-heading); padding-top: 6px; dt { color: var(--text-heading); font-weight: 800; } dd { font-size: 0.9rem; } }
.without dl.sum > div.total { border-top-color: var(--border-color); }
.cmp p.why { color: var(--text-main); i { color: var(--add-c); } }
.cmp-value small { font-size: 0.5em; font-weight: 700; color: var(--text-muted); }
.rh-unit { font-size: 0.42em; margin-left: 8px; -webkit-text-fill-color: #c9f7b3; }
.money-toggle.off { border-color: var(--add-c); color: var(--add-c); }
.cmp-value { font-family: 'Sora', sans-serif; font-weight: 800; font-size: 1.6rem; color: var(--text-heading); .without & { color: var(--text-muted); } }

.res-eq-wrap { display: flex; flex-direction: column; gap: 8px; }
.eqw-head { display: flex; align-items: center; justify-content: space-between; gap: 6px 12px; flex-wrap: wrap; font-size: 0.8rem; font-weight: 700; color: var(--text-muted); }
.info-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--accent-green);
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 0.74rem;
  font-weight: 700;
  &:hover, &.on { border-color: var(--accent-green); background: rgba(var(--accent-green-rgb), 0.08); }
}
.eq-sources {
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-sm);
  font-size: 0.76rem;
  color: var(--text-main);
  .eqs-row { display: flex; gap: 10px; padding: 6px 0; & + .eqs-row { border-top: 1px dashed var(--border-color); } > i { color: var(--add-c); margin-top: 3px; width: 16px; text-align: center; } }
  a { display: inline-block; margin-left: 4px; color: var(--accent-green); font-weight: 700; i { font-size: 0.65rem; } }
  p { margin: 6px 0 0; font-size: 0.7rem; color: var(--text-muted); }
}
.eqs-enter-active, .eqs-leave-active { transition: all 0.25s ease; }
.eqs-enter, .eqs-leave-to { opacity: 0; transform: translateY(-6px); }
.res-eq { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.eq {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 10px;
  border-radius: 16px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  i { color: var(--add-c); font-size: 1.1rem; }
  b { font-family: 'Sora', sans-serif; font-size: 1.15rem; color: var(--text-heading); margin-top: 4px; }
  span { font-size: 0.72rem; color: var(--text-muted); }
}

.res-warn { display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border-radius: 12px; background: rgba(244, 194, 91, 0.14); color: #8a5d10; font-size: 0.76rem; }
.res-note { margin: 0; font-size: 0.72rem; color: var(--text-muted); }

/* ---------- presentation */
.presenting {
  position: fixed;
  inset: 0;
  z-index: 2000;
  overflow: auto;
  padding: 24px clamp(16px, 4vw, 56px) 40px;
  background: var(--bg-primary-gradient, var(--bg-primary));

  .sim-inputs { display: none; }
  .sim-grid { grid-template-columns: minmax(0, 1fr); max-width: 1200px; margin: 0 auto; }
  .sim-results { position: static; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 18px; }
  .res-hero, .res-scene, .res-card { grid-column: 1 / -1; }
  .res-scene { height: 420px; }
  .res-hero { padding: 30px 34px; }
  .rh-value { font-size: clamp(3rem, 7vw, 5rem); }
  .res-note, .res-warn, .res-vs, .res-eq-wrap { grid-column: 1 / -1; }
  .sim-hero { max-width: 1200px; margin: 0 auto 18px; }
}

@media (max-width: 1199px) {
  .sim-grid { grid-template-columns: minmax(0, 1fr); }
  .sim-results { position: static; }
}

@media (max-width: 767px) {
  .presenting .sim-results { grid-template-columns: minmax(0, 1fr); }
  .four { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .three { grid-template-columns: minmax(0, 1fr); }
  .res-compare { grid-template-columns: minmax(0, 1fr); }
  .rh-co2 { width: 100%; }
  .map-wrap { height: 320px; }
  .sa-btn span { display: none; }
  .sa-btn { padding: 0 12px; }
  .sa-btn.primary span, .sa-btn.exit span { display: inline; }
}

@media (max-width: 480px) {
  .two { grid-template-columns: minmax(0, 1fr); }
  .res-eq { grid-template-columns: minmax(0, 1fr); }
  .sp-row { grid-template-columns: 12px minmax(0, 1fr) 30px; }
  .sp-share { grid-column: 2 / -1; }
  .res-scene { height: 300px; }
}
</style>
